import { z } from 'zod'
import { zId, zIdNullable, zIsoDate, zJsonBlob, type JsonBlob } from '../common'

// An autopilot change proposal (the inbox). `status` and `kind` are open
// strings server-side: the documented values get a tone/label in the UI
// registry, unknown ones render verbatim with a neutral treatment.
export const zChangeProposal = z.object({
  id: zId,
  siteId: zIdNullable.optional().default(null),
  adsAccountId: zIdNullable.optional().default(null),
  campaignBindingId: zIdNullable.optional().default(null),
  ruleId: zIdNullable.optional().default(null),
  kind: z.string(),
  status: z.string(),
  payload: zJsonBlob.nullish().default(null),
  rationale: z.string().nullish().default(null),
  // Estimated money impact — drives the inbox sort (descending).
  magnitudeMicros: z.number().nullish().default(null),
  evidence: z.unknown().optional(),
  appliedAt: zIsoDate.nullish().default(null),
  googleResourceName: z.string().nullish().default(null),
})
export type ChangeProposal = z.infer<typeof zChangeProposal>

// ---------------------------------------------------------------------------
// Known-shape views. `payload`/`evidence` stay open blobs on the wire (kind is
// an open string). These are *non-throwing* accessors for the shapes the API
// documents, so the UI can render them structurally without risking a parse
// failure on an undocumented kind or an older/partial proposal.
// ---------------------------------------------------------------------------

// pause_ad_group is the only kind whose payload/evidence shape is documented.
// `campaign_id` is optional: proposals emitted before the structural-count fix
// don't carry it. It is a Google campaign id string, not the internal binding
// id — use `campaignBindingId` to navigate in the app.
// `.nullish()` (not `.optional()`): the Rails API serializes absent jsonb
// fields as explicit `null`, not by omitting the key — matching zIdNullable in
// common.ts. This keeps an unrelated null (e.g. `ads: null`) from failing the
// whole parse and wiping a sibling that *was* present (e.g. `keywords: 15`).
const zPauseAdGroupPayload = z.object({
  resource_name: z.string().nullish(),
  name: z.string().nullish(),
  campaign_id: z.union([z.string(), z.number()]).transform(String).nullish(),
})
const zPauseAdGroupEvidence = z.object({
  keywords: z.number().nullish(),
  ads: z.number().nullish(),
})

export interface PauseAdGroupView {
  name?: string
  resourceName?: string
  campaignId?: string
  keywords?: number
  ads?: number
}

/**
 * Normalized view of a pause_ad_group proposal, or null when the payload isn't
 * that shape. Merges the documented payload and evidence fields.
 */
export function pauseAdGroupView(proposal: ChangeProposal): PauseAdGroupView | null {
  if (proposal.kind !== 'pause_ad_group') return null
  const payload = zPauseAdGroupPayload.safeParse(proposal.payload ?? {})
  const evidence = zPauseAdGroupEvidence.safeParse(proposal.evidence ?? {})
  if (!payload.success) return null
  return {
    name: payload.data.name ?? undefined,
    resourceName: payload.data.resource_name ?? undefined,
    campaignId: payload.data.campaign_id ?? undefined,
    keywords: evidence.success ? (evidence.data.keywords ?? undefined) : undefined,
    ads: evidence.success ? (evidence.data.ads ?? undefined) : undefined,
  }
}

// Payload fields that, when present, read as a human-facing target label. Order
// is priority: the first scalar hit wins. Keeps the inbox "target" column
// meaningful for kinds we don't have a dedicated view for.
const TARGET_LABEL_FIELDS = ['name', 'keyword', 'text', 'headline', 'final_url', 'url'] as const

/** Best-effort primary label for a proposal's target, or null if none found. */
export function proposalTargetLabel(payload: JsonBlob | null | undefined): string | null {
  if (!payload) return null
  for (const field of TARGET_LABEL_FIELDS) {
    const value = payload[field]
    if (typeof value === 'string' && value.trim() !== '') return value
    if (typeof value === 'number') return String(value)
  }
  return null
}
